import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageLoader from '../PageLoader/PageLoader';
import usePageLoader from '../../hooks/usePageLoader';
import './EmailNotifications.css';

/**
 * EmailNotifications component - Allows users to manage email notification preferences.
 * NOTE: No longer renders its own page shell or sidebar — AccountLayout handles that.
 *
 * Notification types:
 *  - promoCodeEmails    : Notify users of discount/promo codes  (optional)
 *  - announcementEmails : Notify users of new campaigns/products (optional)
 *  - orderUpdates       : Order status notifications            (mandatory)
 */
function EmailNotifications() {
    const { currentUser, getUserProfile, updateUserProfile } = useAuth();

    const [preferences, setPreferences] = useState({
        promoCodeEmails:    false,
        announcementEmails: false,
        orderUpdates:       true, // Mandatory, always true
    });
    const [originalPreferences, setOriginalPreferences] = useState({});
    const [loading, setLoading]   = useState(true);
    const [saving,  setSaving]    = useState(false);
    const [status,  setStatus]    = useState(null); // 'saved' | 'error' | null

    // ── Page-level overlay: shows immediately on mount, hides once data is ready ──
    const { pageReady, markPageReady } = usePageLoader();

    useEffect(() => {
        if (currentUser) fetchPreferences();
    }, [currentUser]);

    const fetchPreferences = async () => {
        setLoading(true);
        const data = await getUserProfile(currentUser.uid);
        const prefs = {
            promoCodeEmails:    data?.promoCodeEmails    ?? false,
            announcementEmails: data?.announcementEmails ?? false,
            orderUpdates:       true,
        };
        setPreferences(prefs);
        setOriginalPreferences(prefs);
        setLoading(false);
        markPageReady(); // ← signal overlay to hide
    };

    const handleCheckboxChange = (field, value) => {
        if (field === 'orderUpdates') return;
        setPreferences(prev => ({ ...prev, [field]: value }));
        setStatus(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setStatus(null);
        try {
            await updateUserProfile(currentUser.uid, {
                promoCodeEmails:    preferences.promoCodeEmails,
                announcementEmails: preferences.announcementEmails,
            });
            setOriginalPreferences(preferences);
            setStatus('saved');
        } catch (error) {
            console.error('Error saving preferences:', error);
            setStatus('error');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setPreferences(originalPreferences);
        setStatus(null);
    };

    const hasChanges = JSON.stringify(preferences) !== JSON.stringify(originalPreferences);

    return (
        <>
            {/* ── Global page overlay — shown until data arrives ── */}
            {!pageReady && <PageLoader message="Loading your preferences…" />}

            {/* ── Per-data spinner (original, kept as-is) ── */}
            {loading ? (
                <div className="email-notifications-content">
                    <div className="en-loading">
                        <div className="en-spinner" />
                        <span>Loading your preferences…</span>
                    </div>
                </div>
            ) : (
                <div className="email-notifications-content">
                    <div className="en-header">
                        <h1>Email Notifications</h1>
                        <p className="email-notifications-description">
                            Choose which emails you'd like to receive from us.
                            You can update these preferences at any time.
                        </p>
                    </div>

                    <hr className="email-divider" />

                    <div className="notification-options">

                        {/* ── Promo Code Emails ─────────────────────────────────── */}
                        <label className={`notification-item ${preferences.promoCodeEmails ? 'notification-item--checked' : ''}`}>
                            <div className="notification-item-left">
                                <div className="en-icon en-icon--promo">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
                                        <line x1="7" y1="7" x2="7.01" y2="7"/>
                                    </svg>
                                </div>
                                <div className="notification-label">
                                    <strong>Promo Code Emails</strong>
                                    <span>Get notified of exclusive discount codes and special offers.
                                        <em className="en-example">"Use SAVE20 for 20% off your next order!"</em>
                                    </span>
                                </div>
                            </div>
                            <div className="en-toggle-wrap">
                                <input
                                    type="checkbox"
                                    className="en-toggle-input"
                                    id="promoCodeEmails"
                                    checked={preferences.promoCodeEmails}
                                    onChange={(e) => handleCheckboxChange('promoCodeEmails', e.target.checked)}
                                />
                                <span className="en-toggle" />
                            </div>
                        </label>

                        {/* ── Announcement Emails ───────────────────────────────── */}
                        <label className={`notification-item ${preferences.announcementEmails ? 'notification-item--checked' : ''}`}>
                            <div className="notification-item-left">
                                <div className="en-icon en-icon--announce">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                                    </svg>
                                </div>
                                <div className="notification-label">
                                    <strong>Announcement Emails</strong>
                                    <span>Stay informed about new services, campaigns, and product launches.
                                        <em className="en-example">"Introducing our new Standee printing service!"</em>
                                    </span>
                                </div>
                            </div>
                            <div className="en-toggle-wrap">
                                <input
                                    type="checkbox"
                                    className="en-toggle-input"
                                    id="announcementEmails"
                                    checked={preferences.announcementEmails}
                                    onChange={(e) => handleCheckboxChange('announcementEmails', e.target.checked)}
                                />
                                <span className="en-toggle" />
                            </div>
                        </label>

                        {/* ── Order Updates (mandatory) ─────────────────────────── */}
                        <label className="notification-item notification-item--disabled">
                            <div className="notification-item-left">
                                <div className="en-icon en-icon--order">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M12 5l7 7-7 7"/>
                                    </svg>
                                </div>
                                <div className="notification-label">
                                    <strong>Order Updates <span className="notification-required">*</span></strong>
                                    <span>Important notifications about your order status and delivery.
                                        <em className="en-required-note">Required — cannot be disabled.</em>
                                    </span>
                                </div>
                            </div>
                            <div className="en-toggle-wrap">
                                <input
                                    type="checkbox"
                                    className="en-toggle-input"
                                    checked={true}
                                    disabled
                                    readOnly
                                />
                                <span className="en-toggle en-toggle--locked" />
                            </div>
                        </label>

                    </div>

                    {/* ── Status feedback ───────────────────────────────────────── */}
                    {status === 'saved' && (
                        <div className="en-status en-status--success">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            Preferences saved successfully.
                        </div>
                    )}
                    {status === 'error' && (
                        <div className="en-status en-status--error">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            Failed to save. Please try again.
                        </div>
                    )}

                    {/* ── Actions ───────────────────────────────────────────────── */}
                    <div className="notification-actions">
                        <button
                            className="btn-save"
                            onClick={handleSave}
                            disabled={saving || !hasChanges}
                        >
                            {saving ? (
                                <><span className="en-btn-spinner" /> Saving…</>
                            ) : 'Save Preferences'}
                        </button>
                        <button
                            className="btn-cancel"
                            onClick={handleCancel}
                            disabled={saving || !hasChanges}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default EmailNotifications;